'use client';

import { FirebaseError } from 'firebase/app';

export type AppError = {
  type: 'network' | 'auth' | 'permission' | 'validation' | 'unknown';
  message: string;
  originalError?: Error;
  retryable: boolean;
};

/**
 * Maps Firebase errors to user-friendly messages
 */
export function mapFirebaseError(error: FirebaseError): AppError {
  const errorMap: Record<string, { message: string; type: AppError['type']; retryable: boolean }> = {
    // Auth errors
    'auth/user-not-found': {
      message: 'No account found with this email address',
      type: 'auth',
      retryable: false,
    },
    'auth/wrong-password': {
      message: 'Incorrect password',
      type: 'auth',
      retryable: false,
    },
    'auth/invalid-email': {
      message: 'Please enter a valid email address',
      type: 'validation',
      retryable: false,
    },
    'auth/user-disabled': {
      message: 'Your account has been disabled. Please contact support.',
      type: 'auth',
      retryable: false,
    },
    'auth/too-many-requests': {
      message: 'Too many failed login attempts. Please try again later.',
      type: 'auth',
      retryable: true,
    },
    'auth/network-request-failed': {
      message: 'Network connection failed. Please check your internet connection.',
      type: 'network',
      retryable: true,
    },
    
    // Firestore errors
    'permission-denied': {
      message: 'You do not have permission to perform this action',
      type: 'permission',
      retryable: false,
    },
    'not-found': {
      message: 'The requested resource was not found',
      type: 'unknown',
      retryable: false,
    },
    'already-exists': {
      message: 'This resource already exists',
      type: 'validation',
      retryable: false,
    },
    'resource-exhausted': {
      message: 'Too many requests. Please try again later.',
      type: 'unknown',
      retryable: true,
    },
    'unauthenticated': {
      message: 'You must be signed in to perform this action',
      type: 'auth',
      retryable: false,
    },
    'unavailable': {
      message: 'Service is temporarily unavailable. Please try again.',
      type: 'network',
      retryable: true,
    },
  };

  const mappedError = errorMap[error.code];
  
  return {
    type: mappedError?.type || 'unknown',
    message: mappedError?.message || error.message || 'An unexpected error occurred',
    originalError: error,
    retryable: mappedError?.retryable || false,
  };
}

/**
 * Maps network errors to user-friendly messages
 */
export function mapNetworkError(error: Error): AppError {
  const message = error.message.toLowerCase();
  
  if (message.includes('failed to fetch') || 
      message.includes('networkerror') || 
      message.includes('err_network')) {
    return {
      type: 'network',
      message: 'Unable to connect to the server. Please check your internet connection and try again.',
      originalError: error,
      retryable: true,
    };
  }
  
  if (message.includes('timeout')) {
    return {
      type: 'network',
      message: 'The request timed out. Please try again.',
      originalError: error,
      retryable: true,
    };
  }
  
  return {
    type: 'unknown',
    message: 'An unexpected error occurred. Please try again.',
    originalError: error,
    retryable: true,
  };
}

/**
 * Universal error handler that maps any error to AppError
 */
export function handleError(error: unknown): AppError {
  console.error('Error caught by handleError:', error);
  
  // Firebase errors
  if (error instanceof Error && 'code' in error) {
    return mapFirebaseError(error as FirebaseError);
  }
  
  // Standard errors
  if (error instanceof Error) {
    return mapNetworkError(error);
  }
  
  // String errors
  if (typeof error === 'string') {
    return {
      type: 'unknown',
      message: error,
      retryable: false,
    };
  }
  
  // Unknown errors
  return {
    type: 'unknown',
    message: 'An unexpected error occurred',
    retryable: false,
  };
}

/**
 * Retry utility for retryable operations
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delayMs: number = 1000
): Promise<T> {
  let lastError: unknown;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      const appError = handleError(error);
      
      // Don't retry if error is not retryable
      if (!appError.retryable) {
        throw error;
      }
      
      // Don't retry on last attempt
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
      console.warn(`Retry attempt ${attempt}/${maxRetries} for:`, appError.message);
    }
  }
  
  throw lastError;
}

/**
 * Check if user is online
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

/**
 * Listen to network status changes
 */
export function onNetworkStatusChange(
  onOnline: () => void,
  onOffline: () => void
): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handleOnline = () => {
    console.log('Network: Online');
    onOnline();
  };
  
  const handleOffline = () => {
    console.log('Network: Offline');
    onOffline();
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

